import { useTranslator } from '@bluebeam/react-i18n';
import React, { CSSProperties, useEffect } from 'react';
/** Not exactly sure why, but Storybook breaks if we import this from a barrel */
import useOnClickOutside from 'hooks/useOnOutsideClick';
import ChevronLeft from '../../Icons/ChevronLeft';
import IconButton from '../IconButton/IconButton';
import { ModalContextProvider } from './ModalContextProvider';

const modalSizeStyles = {
  small: {
    width: '336px',
  },
  medium: {
    width: '540px',
  },
  large: {
    width: '720px',
  },
  xlarge: {
    maxHeight: '90vh',
    margin: '5vh',
    height: '90vh',
    width: '95%',
  },
};

type ModalProps = {
  /** The title to display in the modal header */
  title: string;

  /** The variant of the modal content window size */
  size: 'small' | 'medium' | 'large' | 'xlarge';

  /** The callback to invoke when ESC key is pressed, X is clicked or when area outside modal is clicked. */
  onDismiss: () => void;

  showBackLink?: boolean;

  showOverflow?: boolean;

  innerScroll?: boolean;

  style?: CSSProperties;

  overlayStyle?: CSSProperties;

  // hide close button
  disableCloseBtn?: boolean;

  /** Optional text to provide to the back button */
  backButtonText?: string;
};

/** A modal is a type of **molecule** that combines a `<ModalFooter />` component with a `<ModalContent />` component to display inside a window over the top of the page. */
const Modal: React.FC<ModalProps> = ({
  title,
  children,
  onDismiss,
  size,
  showOverflow,
  showBackLink,
  innerScroll = false,
  style,
  disableCloseBtn,
  overlayStyle,
  backButtonText,
}) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, () => onDismiss());

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, []);

  return (
    <ModalContextProvider>
      <div
        id="modal-overlay"
        className="modal-overlay"
        style={{
          ...overlayStyle,
        }}
      >
        <div
          id="modal-container"
          className="modal-container"
          role="dialog"
          style={{
            overflow: showOverflow ? 'visible' : 'hidden',
            ...modalSizeStyles[size],
            ...style,
          }}
          ref={modalRef}
          aria-labelledby={title}
        >
          <header className="modal-header">
            {showBackLink ? (
              <div className="back-link row items-center" onClick={onDismiss}>
                <span className="back-link-icon">
                  <ChevronLeft width="11px" height="11px" />
                </span>
                {backButtonText}
              </div>
            ) : (
              <h2 className="modal-title">{title}</h2>
            )}
            {!disableCloseBtn && (
              <IconButton
                className="modal-header-close-btn"
                aria="Close"
                onClick={onDismiss}
                size="small"
                type="button"
                variant="icon-only"
              >
                <svg fill="currentColor" viewBox="-5 -5 20 20">
                  <path d="M9.002,0.157l-4.002,4.001l-3.998,-3.998c-0.238,-0.219 -0.602,-0.212 -0.828,0.014l-0.043,0.049c-0.188,0.233 -0.174,0.576 0.043,0.793l3.984,3.984l-3.998,3.998c-0.219,0.238 -0.212,0.602 0.014,0.828c0.226,0.226 0.59,0.233 0.824,0.017l4.002,-4.002l3.998,3.999c0.238,0.219 0.602,0.212 0.828,-0.014l0.043,-0.049c0.188,-0.233 0.174,-0.576 -0.043,-0.793l-3.985,-3.984l3.999,-3.998c0.219,-0.238 0.212,-0.602 -0.014,-0.828c-0.226,-0.226 -0.59,-0.233 -0.824,-0.017Z" />
                </svg>
              </IconButton>
            )}
          </header>
          {showBackLink && <h2 className="back-link">{title}</h2>}
          {innerScroll ? (
            children
          ) : (
            <div className="modal-body">{children}</div>
          )}
        </div>
      </div>
    </ModalContextProvider>
  );
};

export default Modal;
